import { Link } from 'react-router-dom';

import { CONTACT_EMAIL, ExtLink, LegalLayout, Section } from './LegalLayout';

const LIST = 'list-disc space-y-2 pl-5 marker:text-white/30';

export default function TermsOfService() {
	return (
		<LegalLayout title="Terms of Service" effectiveDate="September 21, 2026">
			<p>
				These terms govern your use of Enflux, a social media management platform available at enflux.cc. By
				creating an account or using Enflux, you agree to these terms. If you use Enflux on behalf of a
				company or agency, you agree to them on its behalf.
			</p>

			<Section title="Your account">
				<ul className={LIST}>
					<li>Provide accurate information and keep your password secure.</li>
					<li>You are responsible for activity under your account and in the workspaces you manage.</li>
					<li>
						Agency owners and admins control who can access each workspace and what role they have. Tell us
						right away if you believe your account has been compromised.
					</li>
				</ul>
			</Section>

			<Section title="Connected platforms">
				<p>
					Enflux connects to third-party platforms — including Facebook, Instagram, LinkedIn, X, TikTok,
					YouTube, and Google Business Profile — using the permissions you grant. You must have the right to
					manage every account you connect, and your use of each platform through Enflux remains subject to
					that platform's own terms and policies.
				</p>
				<p>
					Enflux uses YouTube API Services. By connecting a YouTube channel, you agree to be bound by the{' '}
					<ExtLink href="https://www.youtube.com/t/terms">YouTube Terms of Service</ExtLink>.
				</p>
				<p>
					Platforms can change or limit their APIs at any time. We are not responsible for a platform
					rejecting, delaying, or removing content, or for changes in the data a platform makes available.
				</p>
			</Section>

			<Section title="Your content">
				<p>
					You keep ownership of everything you upload or create in Enflux. You give us permission to store,
					process, and transmit that content only as needed to provide the service — for example, to publish
					it to the accounts you choose. You are responsible for making sure you have the rights to the content
					you publish, including images, video, music, and trademarks.
				</p>
			</Section>

			<Section title="AI features">
				<p>
					Enflux can generate captions, hashtags, images, content ideas, and report summaries with AI. AI output
					can be inaccurate or unsuitable, so review it before you publish. You are responsible for the content
					you choose to publish.
				</p>
			</Section>

			<Section title="Acceptable use">
				<p>You agree not to use Enflux to:</p>
				<ul className={LIST}>
					<li>Publish spam, or content that is unlawful, deceptive, harassing, or infringes others' rights.</li>
					<li>Violate the terms or policies of any connected platform.</li>
					<li>Access accounts or data you are not authorized to manage.</li>
					<li>Interfere with, overload, reverse engineer, or attempt to gain unauthorized access to Enflux.</li>
				</ul>
				<p>We may suspend or terminate accounts that break these rules.</p>
			</Section>

			<Section title="Privacy">
				<p>
					How we handle your information is described in our{' '}
					<Link to="/privacy" className="text-[#00c7f9] hover:underline">
						Privacy Policy
					</Link>
					.
				</p>
			</Section>

			<Section title="Plans and payment">
				<p>
					Paid plans are billed as agreed when you subscribe. Fees are non-refundable except where required by
					law. We will give you notice before any price change takes effect for your plan.
				</p>
			</Section>

			<Section title="Termination">
				<p>
					You can stop using Enflux at any time and ask us to delete your account. We may suspend or end access
					if you violate these terms. When an account is closed, we delete its data as described in the Privacy
					Policy.
				</p>
			</Section>

			<Section title="Disclaimers and liability">
				<p>
					Enflux is provided “as is” and “as available.” We work to keep it reliable, but we do not guarantee
					that it will be uninterrupted or error-free, or that any post will be published at an exact time. To
					the fullest extent permitted by law, Enflux will not be liable for indirect, incidental, or
					consequential damages, or for lost profits or data, and our total liability for any claim is limited
					to the amount you paid us in the 12 months before the claim.
				</p>
			</Section>

			<Section title="Changes to these terms">
				<p>
					We may update these terms. If we make material changes, we will update the effective date above and
					notify account owners. Continuing to use Enflux after changes take effect means you accept them.
				</p>
			</Section>

			<Section title="Contact">
				<p>
					Questions about these terms:{' '}
					<a href={`mailto:${CONTACT_EMAIL}`} className="text-[#00c7f9] hover:underline">
						{CONTACT_EMAIL}
					</a>
				</p>
			</Section>
		</LegalLayout>
	);
}
